import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-48bd2536"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/_createTextVNode("Loading results")
const _hoisted_2 = /*#__PURE__*/_createTextVNode("Error loading results")
const _hoisted_3 = /*#__PURE__*/_createTextVNode("No results")
const _hoisted_4 = { class: "title" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = {
  class: "description truncate-3",
  tabindex: "0"
}
const _hoisted_7 = {
  key: 0,
  class: "names truncate-1",
  tabindex: "0"
}
const _hoisted_8 = {
  key: 1,
  class: "ids truncate-1",
  tabindex: "0"
}
const _hoisted_9 = /*#__PURE__*/_createTextVNode(" to ")
const _hoisted_10 = /*#__PURE__*/_createTextVNode(" of ")
const _hoisted_11 = /*#__PURE__*/_createTextVNode(" results ")
const _hoisted_12 = ["disabled", "onClick"]
const _hoisted_13 = { key: 0 }

import { ref, computed, watch } from "vue";
import { groupBy, isEqual, kebabCase, sortBy, startCase, uniq } from "lodash";
import AppSelectAutocomplete from "@/components/AppSelectAutocomplete.vue";
import { Options as AutocompleteOptions } from "@/components/AppSelectAutocomplete";
import AppWrapper from "@/components/AppWrapper.vue";
import {
  getAutocompleteResults,
  getSearchResults,
  SearchResults,
} from "@/api/node-search";
import AppSelectMulti from "@/components/AppSelectMulti.vue";
import { Options as MultiOptions } from "@/components/AppSelectMulti";
import { useRoute, useRouter } from "vue-router";
import { filtersToQuery } from "@/api/facets";
import { useQuery } from "@/util/composables";
import { appTitle } from "@/global/meta";
import { history, addEntry, deleteEntry } from "@/global/history";

/** route info */

export default /*#__PURE__*/_defineComponent({
  __name: 'NodeSearch',
  setup(__props) {

const router = useRouter();
const route = useRoute();

/** submitted search text */
const search = ref(String(route.query.search || ""));
/** current page number */
const page = ref(0);
/** results per page */
const perPage = ref(10);
/** filters (facets) for search */
const availableFilters = ref<Record<string, MultiOptions>>({});
const activeFilters = ref<Record<string, MultiOptions>>({});

/** when user focuses text box */
async function onFocus() {
  /** navigate to explore page */
  await router.push({ ...route, name: "Explore" });
  /** refocus box */
  document?.querySelector("input")?.focus();
}

/** when user "submits" text box */
function onChange(value: string) {
  search.value = value;
  page.value = 0;
}

/** when user deletes entry in textbox */
function onDelete(value: string) {
  deleteEntry(value);
}

/** when user changes active filters */
function onFilterChange() {
  page.value = 0;
  getResults(false);
}

/** get autocomplete results */
async function getAutocomplete(search: string): Promise<AutocompleteOptions> {
  /** if something typed in, get autocomplete options from backend */
  if (search.trim()) return await getAutocompleteResults(search);

  /**
   * otherwise, if search box focused and nothing typed in, show some useful
   * entries
   */

  /** show top N entries in each category */
  const top = 5;

  /** recent searches */
  const recent = uniq([...history.value].reverse())
    .slice(0, top)
    .map((search) => ({
      name: search,
      icon: "clock-rotate-left",
      tooltip: "One of your recent node searches",
    }));

  /** most popular searches */
  const popular = sortBy(
    Object.entries(groupBy(history.value)).map(([search, matches]) => ({
      search,
      count: matches.length,
    })),
    "count"
  )
    .filter(({ count }) => count >= 3)
    .reverse()
    .slice(0, top)
    .map(({ search }) => ({
      name: search,
      icon: "person-running",
      tooltip: "One of your frequent node searches",
    }));

  /** example searches */
  const examples = [
    "Marfan syndrome",
    "SSH",
    "Multicystic kidney dysplasia",
  ].map((search) => ({
    name: search,
    icon: "lightbulb",
    tooltip: "Example search",
  }));

  return [...recent, ...popular, ...examples];
}

/** get search results */
const {
  query: getResults,
  data: results,
  isLoading,
  isError,
} = useQuery(
  async function (
    /**
     * whether to perform "fresh" search, without filters/pagination/etc. true
     * when search text changes, false when filters/pagination/etc change.
     */
    fresh: boolean
  ): Promise<SearchResults> {
    /** get results from api */
    const response = await getSearchResults(
      search.value,
      fresh ? undefined : filtersToQuery(availableFilters.value),
      fresh ? undefined : filtersToQuery(activeFilters.value),
      fresh ? undefined : from.value
    );

    return response;
  },

  /** default value */
  { count: 0, results: [], facets: {} },

  /** on success */
  (response, [fresh]) => {
    /** update filters from facets returned from api, if a "fresh" search */
    if (fresh) {
      availableFilters.value = { ...response.facets };
      activeFilters.value = { ...response.facets };
    }

    /** add search to history */
    addEntry(search.value);
  }
);

/** "x of n" pages */
const from = computed((): number => page.value * perPage.value);
const to = computed(
  (): number => from.value + results.value.results.length - 1
);

/** pages of results */
const pages = computed((): Array<Array<number>> => {
  /** get full list of pages */
  const pages = Array(Math.ceil(results.value.count / perPage.value))
    .fill(0)
    .map((_, i) => i);

  /** make shorter pages list */
  let list = [
    /** first few pages */
    0,
    1,
    2,
    /** current few pages */
    page.value - 1,
    page.value,
    page.value + 1,
    /** last few pages */
    pages.length - 3,
    pages.length - 2,
    pages.length - 1,
  ];

  /** sort, deduplicate, and clamp list */
  list.sort((a, b) => a - b);
  list = uniq(list).filter((page) => page >= 0 && page <= pages.length - 1);

  /** split into sub lists where page numbers are not sequential */
  const splitList: Array<Array<number>> = [[]];
  for (let index = 0; index < list.length; index++) {
    if (list[index - 1] && list[index] - list[index - 1] > 1)
      splitList.push([]);
    splitList[splitList.length - 1].push(list[index]);
  }

  return splitList;
});

/** when route changes */
watch(
  () => route.query.search,
  async () => {
    /** update search text from route */
    search.value = String(route.query.search || "");
    /** update document title */
    if (search.value) appTitle.value = [`"${search.value}"`];
    /** refetch search */
    await getResults(true);
  },
  { immediate: true, flush: "post" }
);

/** when search changes */
watch(search, async () => {
  /** update url */
  const query: Record<string, string> = {};
  if (search.value) query.search = search.value;
  await router.push({ ...route, name: "Explore", query });
});

/** when start page changes */
watch(from, () => getResults(false));

/**
 * hide counts in filter dropdowns if any filtering being done. see
 * https://github.com/monarch-initiative/monarch-ui-new/issues/87
 */
const showCounts = computed(() =>
  isEqual(activeFilters.value, availableFilters.value)
);

return (_ctx: any,_cache: any) => {
  const _component_AppFlex = _resolveComponent("AppFlex")!
  const _component_AppStatus = _resolveComponent("AppStatus")!
  const _component_AppIcon = _resolveComponent("AppIcon")!
  const _component_AppLink = _resolveComponent("AppLink")!
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_AppSection = _resolveComponent("AppSection")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(AppWrapper, {
      tag: "AppSection",
      wrap: _ctx.$route.name !== 'Home'
    }, {
      default: _withCtx(() => [
        _createVNode(AppSelectAutocomplete, {
          "model-value": search.value,
          name: "Node search",
          placeholder: "Search for a gene, disease, phenotype, etc.",
          options: getAutocomplete,
          onFocus: onFocus,
          onChange: onChange,
          onDelete: onDelete
        }, null, 8, ["model-value"]),
        (Object.keys(availableFilters.value).length)
          ? (_openBlock(), _createBlock(_component_AppFlex, { key: 0 }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableFilters.value, (filter, name, index) => {
                  return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                    (filter.length)
                      ? _withDirectives((_openBlock(), _createBlock(AppSelectMulti, {
                          key: 0,
                          modelValue: activeFilters.value[name],
                          "onUpdate:modelValue": ($event: any) => ((activeFilters.value[name]) = $event),
                          name: `${name}`,
                          options: availableFilters.value[name],
                          "show-counts": _unref(showCounts),
                          onChange: onFilterChange
                        }, null, 8, ["modelValue", "onUpdate:modelValue", "name", "options", "show-counts"])), [
                          [_directive_tooltip, `${_unref(startCase)(name)} filter`]
                        ])
                      : _createCommentVNode("", true)
                  ], 64))
                }), 128))
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["wrap"]),
    (_ctx.$route.name !== 'Home')
      ? (_openBlock(), _createBlock(_component_AppSection, { key: 0 }, {
          default: _withCtx(() => [
            (_unref(isLoading))
              ? (_openBlock(), _createBlock(_component_AppStatus, {
                  key: 0,
                  code: "loading"
                }, {
                  default: _withCtx(() => [
                    _hoisted_1
                  ]),
                  _: 1
                }))
              : (_unref(isError))
                ? (_openBlock(), _createBlock(_component_AppStatus, {
                    key: 1,
                    code: "error"
                  }, {
                    default: _withCtx(() => [
                      _hoisted_2
                    ]),
                    _: 1
                  }))
                : (!_unref(results).results.length)
                  ? (_openBlock(), _createBlock(_component_AppStatus, {
                      key: 2,
                      code: "warning"
                    }, {
                      default: _withCtx(() => [
                        _hoisted_3
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(results).results, (result, index) => {
              return (_openBlock(), _createBlock(_component_AppFlex, {
                key: index,
                direction: "col",
                gap: "small",
                "h-align": "stretch"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_4, [
                    _withDirectives(_createVNode(_component_AppIcon, {
                      icon: `category-${_unref(kebabCase)(result.category)}`,
                      class: "type"
                    }, null, 8, ["icon"]), [
                      [_directive_tooltip, _unref(startCase)(result.category)]
                    ]),
                    _createVNode(_component_AppLink, {
                      to: `/${_unref(kebabCase)(result.category)}/${result.id}`,
                      class: "name"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("span", {
                          innerHTML: result.highlight
                        }, null, 8, _hoisted_5)
                      ]),
                      _: 2
                    }, 1032, ["to"]),
                    _withDirectives(_createVNode(_component_AppButton, {
                      class: "id",
                      text: result.id,
                      icon: "hashtag",
                      design: "small",
                      copy: true,
                      color: "secondary"
                    }, null, 8, ["text"]), [
                      [_directive_tooltip, 'Node ID (click to copy)']
                    ])
                  ]),
                  _createElementVNode("p", _hoisted_6, _toDisplayString(result.description || "No description available"), 1),
                  (result.altNames?.length)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(result.altNames.join("   ")), 1))
                    : _createCommentVNode("", true),
                  (result.altIds?.length)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(result.altIds.join("   ")), 1))
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1024))
            }), 128)),
            (_unref(results).results.length)
              ? (_openBlock(), _createBlock(_component_AppFlex, {
                  key: 3,
                  direction: "col"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", null, [
                      _createElementVNode("strong", null, _toDisplayString(_unref(from) + 1), 1),
                      _hoisted_9,
                      _createElementVNode("strong", null, _toDisplayString(_unref(to) + 1), 1),
                      _hoisted_10,
                      _createElementVNode("strong", null, _toDisplayString(_unref(results).count), 1),
                      _hoisted_11
                    ]),
                    _createVNode(_component_AppFlex, { gap: "small" }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(pages), (list, index) => {
                          return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(list, (pageNumber) => {
                              return _withDirectives((_openBlock(), _createElementBlock("button", {
                                key: pageNumber,
                                class: "page-button",
                                disabled: pageNumber === page.value,
                                onClick: ($event: any) => (page.value = pageNumber)
                              }, [
                                _createTextVNode(_toDisplayString(pageNumber + 1), 1)
                              ], 8, _hoisted_12)), [
                                [_directive_tooltip, `Go to page ${pageNumber + 1} of results`]
                              ])
                            }), 128)),
                            (index !== _unref(pages).length - 1)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_13, "..."))
                              : _createCommentVNode("", true)
                          ], 64))
                        }), 128))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})